import { parseChatPush } from "./chatPush";
import { chatKeys } from "@/composables/queries/useChat";

const objectID = (value) => typeof value === "string" && /^[a-f0-9]{24}$/.test(value);

export function parseNotificationPush(data, now = Date.now()) {
    if (!data || !["check_reminder", "announcement"].includes(data.type)) return null;
    if (
        typeof data.tenant !== "string" ||
        !/^[a-z0-9][a-z0-9-]{0,62}$/.test(data.tenant) ||
        !objectID(data.user_id)
    )
        return null;
    const expires = Number(data.expires_at);
    if (!Number.isSafeInteger(expires) || expires <= now) return null;
    if (data.type === "announcement") {
        if (!objectID(data.announcement_id)) return null;
        return {
            tenant: data.tenant,
            user: data.user_id,
            expires,
            keys: [["announcements"]],
            route: { path: `/announcements/${data.announcement_id}` }
        };
    }
    const locations = typeof data.location_ids === "string" ? data.location_ids.split(",") : [];
    const due = Number(data.due_at);
    if (
        !objectID(data.form_id) ||
        !objectID(data.schedule_id) ||
        !locations.length ||
        !locations.every(objectID) ||
        !Number.isSafeInteger(due) ||
        due <= 0 ||
        due > now ||
        expires > due + 15 * 60 * 1000
    )
        return null;
    return {
        tenant: data.tenant,
        user: data.user_id,
        expires,
        keys: [["check-forms"], ["check-schedules"]],
        route: {
            path: "/checks",
            query: {
                form: data.form_id,
                schedule: data.schedule_id,
                locations: locations.join(","),
                due: String(due),
                expires: String(expires)
            }
        }
    };
}

// Keep cold-start taps until authentication is restored. A notification never
// changes account/tenant or bypasses the destination's normal authorization.
export async function installNotificationPush(options) {
    const { plugin, session, navigate, invalidate, watchSession } = options;
    const handles = [];
    let pending = null;
    let stopped = false;
    let revision = 0;
    const matches = (target) => {
        const actor = session();
        return (
            !stopped &&
            actor.ready &&
            (!target.chat || actor.chatEnabled === true) &&
            actor.tenant === target.tenant &&
            actor.user === target.user &&
            ["admin", "user"].includes(actor.role) &&
            target.expires > Date.now()
        );
    };
    const parse = (data) => {
        const target = parseNotificationPush(data);
        if (target) return target;
        const chat = parseChatPush(data);
        return chat
            ? {
                  ...chat,
                  chat: true,
                  expires: Infinity,
                  keys: [chatKeys.all],
                  route: {
                      path: "/chat",
                      query: {
                          group: chat.group,
                          ...(chat.message ? { message: chat.message } : {})
                      }
                  }
              }
            : null;
    };
    const flush = async () => {
        if (!pending || !session().ready) return;
        const target = pending;
        pending = null;
        const attempt = ++revision;
        if (!matches(target)) return;
        try {
            await Promise.all(target.keys.map((key) => invalidate(key)));
            if (attempt === revision && matches(target)) await navigate(target.route);
        } catch {
            /* Keep normal app navigation available when offline. */
        }
    };
    try {
        handles.push(
            await plugin.addListener("pushNotificationReceived", (notification) => {
                const target = parse(notification.data);
                if (target && matches(target))
                    void Promise.all(target.keys.map((key) => invalidate(key))).catch(() => {});
            })
        );
        handles.push(
            await plugin.addListener("pushNotificationActionPerformed", (action) => {
                const target = parse(action.notification?.data);
                if (target && !stopped) {
                    pending = target;
                    void flush();
                }
            })
        );
        const stopWatch = watchSession(() => {
            void flush();
        });
        return async () => {
            stopped = true;
            pending = null;
            revision++;
            stopWatch();
            await Promise.all(handles.map((handle) => handle.remove()));
        };
    } catch (error) {
        await Promise.all(handles.map((handle) => handle.remove()));
        throw error;
    }
}
